import { useGetJob } from '@/api'
import { ROUTER_PATHS } from '@/lib/constants'
import { useAbility } from '@/lib/permissions'
import {
  formatUsPhoneToHtml,
  getContactEmails,
  getContactPhoneNumber,
} from '@eigtech/contacts-util'
import { JobIdSchema } from '@eigtech/restoration-types'
import { getJobLabelFromId } from '@eigtech/restoration-util'
import { getContactName, useGetContactOfTypeFromJob } from '@eigtech/ui-shared-contacts'
import {
  Box,
  CircularProgress,
  FancyLink,
  HStack,
  Heading,
  Icon,
  IconProps,
  Page,
  PageTabs,
  Skeleton,
  Stack,
  TabList,
  Text,
  forwardRef,
  getGoogleMapQueryLink,
  useBreakpointValue,
} from '@eigtech/ui-shared-dave'
import { formatPhoneNumber } from '@eigtech/ui-shared-phone'
import { BackButton, LinkButton, LinkTab } from '@eigtech/ui-shared-router'
import { Outlet, createFileRoute, useChildMatches } from '@tanstack/react-router'
import { FaMapMarkedAlt } from 'react-icons/fa'
import { IoMdDocument } from 'react-icons/io'
import {
  MdAttachMoney,
  MdInfo,
  MdMeetingRoom,
  MdOutlineStickyNote2,
  MdPhoto,
  MdSchedule,
  MdSearch,
} from 'react-icons/md'
import { z } from 'zod'

const ParamsSchema = z.object({
  jobId: JobIdSchema,
})

export const Route = createFileRoute('/_auth/jobs/$jobId/_layout')({
  component: JobDetail,
  parseParams: ParamsSchema.parse,
})

function JobDetail() {
  const { jobId } = Route.useParams()

  const { isPending, isError } = useGetJob(jobId)

  const backButton = useBreakpointValue({
    base: null,
    md: <BackButton to={ROUTER_PATHS.jobs.route}>← Jobs</BackButton>,
  })

  return (
    <Page
      aboveHeader={backButton}
      belowHeader={<JobDetailMenu />}
      errorMessage="Something went wrong trying to retrieve this job."
      heading={<JobDetailHeader />}
      isError={isError}
      isPending={isPending}
    >
      <Outlet />
    </Page>
  )
}

function JobDetailHeader() {
  const { jobId } = Route.useParams()

  const { data: job, isPending, isFetching } = useGetJob(jobId)

  const { contacts: primaryContacts } = useGetContactOfTypeFromJob({
    contactType: 'primaryContact',
    jobId,
  })
  const primaryContact = primaryContacts?.[0]

  const primaryContactName = getContactName(primaryContact ?? {}, 'No Primary Contact')
  const phone = getContactPhoneNumber(primaryContact ?? {})
  const emails = getContactEmails(primaryContact ?? {})

  const { addressLine1, addressLine2, city, stateOrProvince, zipOrPostalCode } =
    job?.jobLocation?.address ?? {}
  const fullAddress = [addressLine1, addressLine2, city, stateOrProvince, zipOrPostalCode]
    .filter((value) => !!value)
    .join(', ')

  return (
    <Stack spacing="1">
      <HStack>
        <Skeleton isLoaded={!isPending}>
          <Heading as="h1" size="lg">
            {getJobLabelFromId(jobId)}
          </Heading>
        </Skeleton>

        {!!(isFetching || isPending) && <CircularProgress isIndeterminate size="2rem" />}
      </HStack>

      <Stack spacing="0">
        <Text fontWeight="semibold">{job ? primaryContactName : isPending}</Text>

        <Skeleton isLoaded={!isPending}>
          {isPending
            ? 'Loading phone...'
            : !!phone && (
                <FancyLink href={`tel:${formatUsPhoneToHtml(phone)}`} iconPosition="left">
                  {formatPhoneNumber(phone)}
                </FancyLink>
              )}
        </Skeleton>
        <Skeleton isLoaded={!isPending}>
          {isPending
            ? 'Loading email...'
            : !!emails &&
              emails.map((email) => (
                <FancyLink key={email} href={`mailto:${email}`} iconPosition="left">
                  {email}
                </FancyLink>
              ))}
        </Skeleton>
        <Skeleton isLoaded={!isPending}>
          {job?.jobLocation?.address ? (
            <FancyLink
              customIcon={MapIcon}
              href={getGoogleMapQueryLink(job.jobLocation.address)}
              iconPosition="left"
              isExternal
            >
              {fullAddress}
            </FancyLink>
          ) : (
            'Loading...'
          )}
        </Skeleton>
      </Stack>
    </Stack>
  )
}

function JobDetailMenu() {
  const { jobId } = Route.useParams()

  const ability = useAbility()

  const matches = useChildMatches()

  const permissibleRoutes = routes.filter((route) =>
    'permissions' in route
      ? ability.can(route.permissions.action, route.permissions.subject)
      : route
  )

  const tabIndex =
    permissibleRoutes.findIndex((route) => matches.some((match) => match.routeId === route.id)) ?? 0

  const isJobHome = matches.some((match) => match.routeId === '/_auth/jobs/$jobId/_layout/')

  return (
    <>
      <Box bg="white" borderRadius="md" display={{ base: 'block', md: 'none' }}>
        <LinkButton
          params={{ jobId }}
          size="sm"
          to={isJobHome ? ROUTER_PATHS.jobs.route : ROUTER_PATHS.jobDetail.route}
          variant="outline"
          w="full"
        >
          ← {isJobHome ? 'All Jobs' : 'Job Home'}
        </LinkButton>
      </Box>

      <Box display={{ base: 'none', md: 'block' }}>
        <PageTabs index={tabIndex}>
          <TabList>
            {permissibleRoutes.map((tab) => (
              <LinkTab
                key={tab.id}
                data-id={tab.id}
                params={{ jobId }}
                position="relative"
                {...tab.url}
              >
                {tab.label}
              </LinkTab>
            ))}
          </TabList>
        </PageTabs>
      </Box>
    </>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const routes = [
  {
    url: { to: ROUTER_PATHS.jobDetailInfo.route },
    id: ROUTER_PATHS.jobDetailInfo.routeId,
    label: 'Details',
    icon: MdInfo,
  },
  {
    url: { to: ROUTER_PATHS.jobDetailDocuments.route },
    id: ROUTER_PATHS.jobDetailDocuments.routeId,
    label: 'Documents',
    icon: IoMdDocument,
  },
  {
    url: { to: ROUTER_PATHS.jobDetailMedia.route },
    id: ROUTER_PATHS.jobDetailMedia.routeId,
    label: 'Media',
    icon: MdPhoto,
  },
  {
    url: { to: ROUTER_PATHS.jobDetailBilling.route },
    id: ROUTER_PATHS.jobDetailBilling.routeId,
    label: 'Bills',
    icon: MdAttachMoney,
    permissions: { action: 'read', subject: 'bill' },
  },
  {
    url: {
      to: ROUTER_PATHS.jobDetailAppointments.route,
    },
    id: ROUTER_PATHS.jobDetailAppointments.routeId,
    label: 'Appointments',
    icon: MdSchedule,
  },
  {
    url: { to: ROUTER_PATHS.jobDetailNotes.route },
    id: ROUTER_PATHS.jobDetailNotes.routeId,
    label: 'Notes',
    icon: MdOutlineStickyNote2,
    permissions: { action: 'read', subject: 'note' },
  },
  {
    url: { to: ROUTER_PATHS.jobDetailMeetings.route },
    id: ROUTER_PATHS.jobDetailMeetings.routeId,
    label: 'Inspections',
    icon: MdMeetingRoom,
    permissions: { action: 'create', subject: 'meeting' },
  },
  {
    url: { to: ROUTER_PATHS.jobDetailInspections.route },
    id: ROUTER_PATHS.jobDetailInspections.routeId,
    label: 'Inspections (V2)',
    icon: MdSearch,
  },
] as const

const MapIcon = forwardRef<IconProps, 'svg'>((props, ref) => (
  <Icon ref={ref} {...props} as={FaMapMarkedAlt} />
))
